import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext();

const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
    ? `http://${window.location.hostname}:5000`
    : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

const currencyMeta = {
    'INR': { symbol: '₹', locale: 'en-IN', name: 'Indian Rupee' },
    'USD': { symbol: '$', locale: 'en-US', name: 'US Dollar' },
    'EUR': { symbol: '€', locale: 'de-DE', name: 'Euro' },
    'GBP': { symbol: '£', locale: 'en-GB', name: 'British Pound' },
    'AED': { symbol: 'د.إ', locale: 'ar-AE', name: 'UAE Dirham' },
    'SAR': { symbol: '﷼', locale: 'ar-SA', name: 'Saudi Riyal' },
    'SGD': { symbol: 'S$', locale: 'en-SG', name: 'Singapore Dollar' },
    'AUD': { symbol: 'A$', locale: 'en-AU', name: 'Australian Dollar' },
    'CAD': { symbol: 'C$', locale: 'en-CA', name: 'Canadian Dollar' },
    'JPY': { symbol: '¥', locale: 'ja-JP', name: 'Japanese Yen' },
    'CHF': { symbol: 'CHF', locale: 'de-CH', name: 'Swiss Franc' },
    'CNY': { symbol: '¥', locale: 'zh-CN', name: 'Chinese Yuan' },
    'KRW': { symbol: '₩', locale: 'ko-KR', name: 'South Korean Won' },
    'RUB': { symbol: '₽', locale: 'ru-RU', name: 'Russian Ruble' },
    'TRY': { symbol: '₺', locale: 'tr-TR', name: 'Turkish Lira' },
    'ZAR': { symbol: 'R', locale: 'en-ZA', name: 'South African Rand' },
    'NZD': { symbol: 'NZ$', locale: 'en-NZ', name: 'New Zealand Dollar' },
    'BRL': { symbol: 'R$', locale: 'pt-BR', name: 'Brazilian Real' }
};

export function CurrencyProvider({ children }) {
    const [currency, setCurrency] = useState('INR');
    const [isLoading, setIsLoading] = useState(true);
    const { token } = useAuth();

    const fetchCurrency = useCallback(async () => {
        try {
            const cached = sessionStorage.getItem('dineflow_currency');
            if (cached) {
                setCurrency(cached);
                setIsLoading(false);
                return;
            }

            const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
            const params = new URLSearchParams(window.location.search);
            const companyId = params.get('companyId') || params.get('company_id');
            if (companyId) {
                headers['x-company-id'] = companyId;
            }

            const url = `${API_URL}/api/company/currency`;
            const response = await fetch(url, { headers });
            const data = await response.json();

            if (data.success && data.currency) {
                setCurrency(data.currency);
                sessionStorage.setItem('dineflow_currency', data.currency);
            }
        } catch (error) {
            console.error('Failed to fetch currency:', error);
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchCurrency();
    }, [fetchCurrency]);

    const formatPrice = useCallback((item) => {
        // Fallback logic for items that might not have the new 'price' column yet
        const price = item.price || item.price_inr || item.price_usd || 0;
        const meta = currencyMeta[currency] || currencyMeta['INR'];
        const val = parseFloat(price);
        return `${meta.symbol}${isNaN(val) ? '0.00' : val.toFixed(2)}`;
    }, [currency]);

    const formatAmount = useCallback((amount) => {
        const meta = currencyMeta[currency] || currencyMeta['INR'];
        const val = parseFloat(amount);
        return `${meta.symbol}${isNaN(val) ? '0.00' : val.toFixed(2)}`;
    }, [currency]);

    const getCurrencySymbol = useCallback(() => {
        const meta = currencyMeta[currency] || currencyMeta['INR'];
        return meta.symbol;
    }, [currency]);

    const value = {
        currency,
        setCurrency,
        isLoading,
        formatPrice,
        formatAmount,
        getCurrencySymbol,
        currencyMeta,
        refreshCurrency: fetchCurrency
    };


    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
}
